/**
 * Google Analytics 4 — client-side helpers.
 *
 * Companion to the <GoogleAnalytics> component that loads gtag.js. Reports into
 * the SAME data stream as www.gobo.show (one property, one stream) so a landing
 * page → signup journey resolves as a single GA4 session. See docs/analytics.md.
 *
 * ⚠️ We own every page_view. gtag('config', ID) sends one automatically, so the
 * component configures with `send_page_view: false` and this module's
 * trackPageView is the only thing that ever emits one. Turning that off while
 * also firing manually is the GA4 form of the double-count bug MetaPixel has.
 *
 * Deliberately IMPORT-FREE so it is unit-testable with
 * `node --experimental-strip-types` (same constraint as lib/attribution.ts and
 * lib/festivalSpaces.ts). Callers pass an optional error sink rather than this
 * module reaching for Bugsnag.
 */

const MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID || "";
const RELEASE_STAGE = process.env.NEXT_PUBLIC_RELEASE_STAGE || "";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Reported to the caller instead of throwing; analytics never breaks a page. */
export type ErrorSink = (error: unknown, meta?: Record<string, unknown>) => void;

export function gaMeasurementId(): string {
  return MEASUREMENT_ID;
}

/**
 * True only in the browser, with an ID configured, on production.
 *
 * Gated on NEXT_PUBLIC_RELEASE_STAGE rather than NODE_ENV: both deployments are
 * built with `next build`, which sets NODE_ENV=production at runtime regardless,
 * so NODE_ENV cannot tell test from prod. Hotjar gets this wrong and therefore
 * runs on app-test too; don't copy it. apphosting.yaml sets "production",
 * apphosting.test.yaml sets "test".
 */
export function isGaEnabled(): boolean {
  return (
    typeof window !== "undefined" &&
    !!MEASUREMENT_ID &&
    RELEASE_STAGE === "production"
  );
}

/**
 * Query parameters never sent to GA4.
 *
 * These are single-use auth secrets that genuinely appear in this app's URLs —
 * `oobCode` on /auth/action and /reset-password, `token` on the public
 * /preferences/artist page. page_location would otherwise copy a live
 * credential into analytics, where it is retained and visible to anyone with
 * report access. Dropped, not masked, so no length or shape survives either.
 */
const REDACTED_PARAMS = [
  "oobcode",
  "token",
  "id_token",
  "access_token",
  "refresh_token",
  "apikey",
  "code",
  "secret",
  "password",
  "email",
];

/**
 * Strip credential-bearing parameters from a URL or a path+query string.
 *
 * Accepts either an absolute URL or a root-relative path; returns the same
 * shape it was given. Matching is case-insensitive because Firebase spells it
 * `oobCode` while a hand-built link might not.
 */
export function redactUrl(url: string): string {
  if (typeof url !== "string" || url === "") return url;

  const queryStart = url.indexOf("?");
  if (queryStart === -1) return url;

  const base = url.slice(0, queryStart);
  const hashStart = url.indexOf("#", queryStart);
  const query = url.slice(queryStart + 1, hashStart === -1 ? undefined : hashStart);
  const hash = hashStart === -1 ? "" : url.slice(hashStart);

  const kept = query
    .split("&")
    .filter((pair) => {
      if (pair === "") return false;
      const name = pair.split("=")[0].toLowerCase();
      return !REDACTED_PARAMS.includes(name);
    });

  return kept.length > 0 ? `${base}?${kept.join("&")}${hash}` : `${base}${hash}`;
}

/**
 * Ensure the gtag stub and its queue exist.
 *
 * Defined here rather than in an inline <Script> so it cannot race the effects:
 * next/script's afterInteractive may execute after React has already run them,
 * which would drop the first page_view. This matches gtag's own stub exactly —
 * it pushes the `arguments` object — so gtag.js drains the queue in order once
 * it loads.
 */
function ensureGtag(): ((...args: unknown[]) => void) | null {
  if (typeof window === "undefined") return null;
  if (typeof window.gtag !== "function") {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    } as (...args: unknown[]) => void;
  }
  return window.gtag!;
}

/** Queue a raw gtag call. No-op when GA is disabled. */
export function gtag(onError?: ErrorSink, ...args: unknown[]): void {
  if (!isGaEnabled()) return;
  try {
    ensureGtag()?.(...args);
  } catch (error) {
    onError?.(error, { context: "ga:gtag" });
  }
}

/**
 * Configure the stream once per page load.
 *
 * `send_page_view: false` is the whole point — see the header note. Without it
 * every full page load reports two page_views: this config call's own, plus the
 * one trackPageView sends for the same URL.
 */
export function configureGa(onError?: ErrorSink): void {
  if (!isGaEnabled()) return;
  try {
    const g = ensureGtag();
    if (!g) return;
    g("js", new Date());
    g("config", MEASUREMENT_ID, {
      send_page_view: false,
      // Both hosts are subdomains of gobo.show, so the default 'auto' cookie
      // domain writes _ga on .gobo.show and the www session continues into the
      // app with no cross-domain linking. Stated explicitly so it survives a
      // future move to a different root domain, which WOULD need linking.
      cookie_domain: "auto",
    });
  } catch (error) {
    onError?.(error, { context: "ga:configure" });
  }
}

/**
 * Report one page_view for `url` (path + query, or an absolute URL).
 *
 * Credential parameters are stripped first. Returns true when an event was
 * actually queued, which is what the caller's de-duplication keys on.
 */
export function trackPageView(
  url: string,
  title?: string,
  onError?: ErrorSink
): boolean {
  if (!isGaEnabled()) return false;
  try {
    const g = ensureGtag();
    if (!g) return false;
    const safe = redactUrl(url);
    g("event", "page_view", {
      page_location:
        safe.indexOf("http") === 0 ? safe : `${window.location.origin}${safe}`,
      page_path: safe,
      ...(title ? { page_title: title } : {}),
    });
    return true;
  } catch (error) {
    onError?.(error, { context: "ga:pageView" });
    return false;
  }
}
